import { useJamChallengeDetails } from '@/src/store/jam-challenge-details.context';
import { i18nKeys } from '@/src/utils/i18n.utils';
import {
  Box,
  Button,
  ColumnLayout,
  ExpandableSection,
  Popover,
  SpaceBetween,
  StatusIndicator,
} from '@amzn/awsui-components-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import './JamChallengeProperties.scss';

interface ValueWithLabelProps {
  label: string;
  value: string;
}

const ValueWithLabel: React.FC<ValueWithLabelProps> = ({ label, value }) => {
  const { t } = useTranslation();

  return (
    <SpaceBetween direction="horizontal" size="s">
      <Box variant="awsui-key-label">{label}</Box>
      <div style={{ display: 'flex' }}>
        <span className="challenge-output-properties-value">{value}</span>
        <Popover
          size="small"
          position="top"
          triggerType="custom"
          dismissButton={false}
          content={
            <StatusIndicator type="success">
              <span role="alert">{t(i18nKeys.copyButton.popupText, { value })}</span>
            </StatusIndicator>
          }
        >
          <Button
            variant="inline-icon"
            iconName="copy"
            ariaLabel={t(i18nKeys.copyButton.ariaLabel, { value })}
            onClick={() => void navigator.clipboard.writeText(value)}
          />
        </Popover>
      </div>
    </SpaceBetween>
  );
};

interface JamChallengePropertiesProps {
  defaultExpanded?: boolean;
}

export const JamChallengeProperties: React.FC<JamChallengePropertiesProps> = ({ defaultExpanded = false }) => {
  const { t } = useTranslation();
  const { outputProperties, matchedChallengeProgress } = useJamChallengeDetails();
  if (!outputProperties || outputProperties.length === 0) {
    return null;
  }

  if (!matchedChallengeProgress?.started || matchedChallengeProgress?.completed) return null;

  return (
    <div className="challenge-output-properties-container">
      <ExpandableSection
        defaultExpanded={defaultExpanded}
        variant="container"
        headerText={t(i18nKeys.myJams.challenges.details.outputProperties.title)}
      >
        <ColumnLayout columns={2} variant="text-grid">
          {outputProperties.map((property, index) => (
            // Custom properties will not have labels - use the key instead as was done on Jam 1.0
            <ValueWithLabel key={index} label={property.label ?? property.key} value={property.value} />
          ))}
        </ColumnLayout>
      </ExpandableSection>
    </div>
  );
};
